import { Order, ProcurementBatch, OrderItem, ProcurementBatchItem } from '../types';

/**
 * Determines if an order is ready to be packed based on procurement batch collection status
 * 
 * An order is "ready to pack" when:
 * 1. Order status is 'paid'
 * 2. Order is in procurement (inProcurement = true)
 * 3. Order has a procurementBatchId
 * 4. ALL items in the order have sufficient quantities collected in the procurement batch
 */
export function isOrderReadyToPack(
  order: Order, 
  procurementBatches: ProcurementBatch[]
): boolean {
  // Check basic requirements
  if (order.status !== 'paid') {
    return false;
  }

  if (!order.inProcurement || !order.procurementBatchId) {
    return false;
  }

  // Find the corresponding procurement batch
  const procurementBatch = procurementBatches.find(
    batch => batch.id === order.procurementBatchId
  );

  if (!procurementBatch) {
    return false;
  }

  // Check if all order items have sufficient quantities collected
  return areAllOrderItemsCollected(order.items, procurementBatch);
}

/**
 * Checks if all items in an order have been collected in sufficient quantities
 */
export function areAllOrderItemsCollected(
  orderItems: OrderItem[], 
  procurementBatch: ProcurementBatch
): boolean {
  return orderItems.every(orderItem => {
    const requiredQuantity = orderItem.quantity * (orderItem.listingQuantity || 1);
    const collectedQuantity = procurementBatch.actualQuantities[orderItem.id] || 0;
    
    return collectedQuantity >= requiredQuantity;
  });
}

/**
 * Gets the collection status for a specific order item
 */
export function getOrderItemCollectionStatus(
  orderItem: OrderItem,
  procurementBatch: ProcurementBatch
): {
  required: number;
  collected: number;
  isFullyCollected: boolean;
  collectionPercentage: number;
} {
  const requiredQuantity = orderItem.quantity * (orderItem.listingQuantity || 1);
  const collectedQuantity = procurementBatch.actualQuantities[orderItem.id] || 0;
  const isFullyCollected = collectedQuantity >= requiredQuantity;
  const collectionPercentage = requiredQuantity > 0 ? Math.min((collectedQuantity / requiredQuantity) * 100, 100) : 0;

  return {
    required: requiredQuantity,
    collected: collectedQuantity,
    isFullyCollected,
    collectionPercentage
  };
}

/**
 * Gets the overall collection status for an entire order
 */
export function getOrderCollectionStatus(
  order: Order,
  procurementBatch: ProcurementBatch | null
): {
  totalItems: number;
  fullyCollectedItems: number;
  overallPercentage: number;
  isReadyToPack: boolean;
} {
  if (!procurementBatch) {
    return {
      totalItems: order.items.length,
      fullyCollectedItems: 0,
      overallPercentage: 0,
      isReadyToPack: false
    };
  }

  const itemStatuses = order.items.map(item => 
    getOrderItemCollectionStatus(item, procurementBatch)
  );

  const fullyCollectedItems = itemStatuses.filter(status => status.isFullyCollected).length;
  const totalItems = order.items.length;
  const overallPercentage = totalItems > 0 ? (fullyCollectedItems / totalItems) * 100 : 0;
  
  // Ready to pack only if order is paid, in procurement, and all items collected
  const isReadyToPack = order.status === 'paid' && 
                       order.inProcurement && 
                       !!order.procurementBatchId &&
                       fullyCollectedItems === totalItems;

  return {
    totalItems,
    fullyCollectedItems,
    overallPercentage,
    isReadyToPack
  };
}

/**
 * Filters orders to get only those that are ready to pack
 */
export function getReadyToPackOrders(
  orders: Order[],
  procurementBatches: ProcurementBatch[]
): Order[] {
  return orders.filter(order => isOrderReadyToPack(order, procurementBatches));
}

/**
 * Groups orders by their procurement batch and collection status
 */
export function groupOrdersByCollectionStatus(
  orders: Order[],
  procurementBatches: ProcurementBatch[]
): {
  readyToPack: Order[];
  partiallyCollected: Order[];
  notInProcurement: Order[];
  noProcurementBatch: Order[];
} {
  const readyToPack: Order[] = [];
  const partiallyCollected: Order[] = [];
  const notInProcurement: Order[] = [];
  const noProcurementBatch: Order[] = [];

  orders.forEach(order => {
    if (!order.inProcurement) {
      notInProcurement.push(order);
    } else if (!order.procurementBatchId) {
      noProcurementBatch.push(order);
    } else if (isOrderReadyToPack(order, procurementBatches)) {
      readyToPack.push(order);
    } else {
      partiallyCollected.push(order);
    }
  });

  return {
    readyToPack,
    partiallyCollected,
    notInProcurement,
    noProcurementBatch
  };
}

/**
 * Creates a map of procurement batch IDs to batches for quick lookup
 */
export function createProcurementBatchMap(batches: ProcurementBatch[]): Map<string, ProcurementBatch> {
  return new Map(batches.map(batch => [batch.id, batch]));
}

/**
 * Gets unique procurement batch IDs from a list of orders
 */
export function getUniqueProcurementBatchIds(orders: Order[]): string[] {
  const batchIds = new Set<string>();
  
  orders.forEach(order => {
    if (order.procurementBatchId) {
      batchIds.add(order.procurementBatchId);
    }
  });

  return Array.from(batchIds);
}